<template>
  <div v-if="$page" id="page-sustainability">
    <header ref="header" class="header">
      <figure
        v-scroll:parallax="{ refNode: $refs.header, ratio: 0.15 }"
        class="header__background -darken"
      >
        <Image v-if="$page.headerImg" :source="$page.headerImg" />
      </figure>

      <div class="header__content">
        <h1 v-scroll:reveal="{ delay: 550 }" class="header__legend">{{ $page.title }}</h1>
        <h2 v-scroll:reveal="{ delay: 600 }" class="header__title">{{ $page.headerTitleTop }}</h2>
        <h3 v-scroll:reveal="{ delay: 650 }" class="header__title">
          {{ $page.headerTitleBottom }}
        </h3>
      </div>
    </header>

    <section
      :id="$t('navigation.anchors.sustainability.overview')"
      class="section -indented -pad-down-mobile -sustainability"
    >
      <h2 class="section__legend">{{ $page.introLegend }}</h2>

      <div class="section__content">
        <h3 class="section__title">{{ $page.introTitle }}</h3>
        <div class="row">
          <RawHtml :html="$page.introText" class="row__item -span-4" />

          <ul class="row row__item -center">
            <li
              v-for="({ label, value, suffix = '' }, idx) in $page.introStats"
              :key="label"
              v-scroll:reveal="{ delay: 650 + 75 * idx, offset: 0 }"
              class="stat"
            >
              <output class="stat__value">{{ value }} {{ suffix }}</output>
              <p>{{ label }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <ContentSlider
      v-if="$page.prioritySlides?.length"
      :id="$t('navigation.anchors.sustainability.priorities')"
      v-bind="currentPrioritySlide"
      :legend="$page.priorityLegend"
      :current-index="currentPriorityIndex"
      :slides-count="$page.prioritySlides.length"
      :image="currentPrioritySlide.image"
      section-legend-class="-priorities"
      @index-change="handlePriorityIndexChange"
      class="--tablet"
    />

    <section
      v-if="$page.prioritySlides"
      :id="$t('navigation.anchors.sustainability.priorities')"
      class="section mobile-content --phone"
    >
      <MobileContent
        v-for="(content, index) in $page.prioritySlides"
        :key="index"
        v-bind="content"
        :legend="!index && $page.priorityLegend"
        :image="content.image"
      />
    </section>

    <section
      :id="$t('navigation.anchors.sustainability.importance')"
      ref="importance"
      class="section -centered -centered-tablet"
    >
      <div class="banner -pull-mobile">
        <figure
          v-scroll:parallax="{ refNode: $refs.importance, ratio: 0.1 }"
          class="banner__image --tablet"
          :style="{ backgroundImage: `url('${$page.importanceImg.original}')` }"
        />

        <h2 v-scroll:reveal class="banner__legend -align-let-phone">
          {{ $page.importanceLegend }}
        </h2>
      </div>

      <div class="section__content -narrow -push">
        <h3 v-scroll:reveal class="section__title">{{ $page.importanceTitle }}</h3>
        <RawHtml v-scroll:reveal="{ delay: 100 }" :html="$page.importanceText" />
      </div>
    </section>

    <ContentSlider
      v-if="$page.frameworkSlides"
      :id="$t('navigation.anchors.sustainability.framework')"
      :legend="$page.frameworkLegend"
      :title="$page.frameworkTitle"
      :text="$page.frameworkText"
      :slides-count="$page.frameworkSlides.length"
      :current-index="currentFrameworkIndex"
      :image="currentFrameworkSlide.image"
      :slide-title="currentFrameworkSlide.title"
      :slide-text="currentFrameworkSlide.text"
      :is-small="true"
      section-legend-class="-framework"
      @index-change="handleFrameworkIndexChange"
    >
      <Modal
        id="framework-modal"
        class="modal--framework"
        :image="$page.frameworkModalImg"
        :image-alt="currentFrameworkSlide.title"
      >
        <button class="modal__button button -secondary">{{ $page.frameworkButtonText }}</button>
      </Modal>
    </ContentSlider>

    <section :id="$t('navigation.anchors.sustainability.highlights')" class="section -alt">
      <div class="row">
        <div class="section__content row__item">
          <h2 v-scroll:reveal class="section__legend">{{ $page.highlightsLegend }}</h2>
          <h3 v-scroll:reveal="{ delay: 100 }" class="section__title">
            {{ $page.highlightsTitle }}
          </h3>
          <RawHtml v-scroll:reveal="{ delay: 175 }" :html="$page.highlightsText" />
        </div>

        <div class="row__item -span-6 -skip-1">
          <CardSlider :slides="$page.highlightsSlides" image-key="highlightsImg" />
        </div>
      </div>
    </section>

    <section class="section row -tablet">
      <div v-scroll:reveal class="cta-card">
        <Image v-if="$page.ctaProductsImg" class="cta-card__image" :source="$page.ctaProductsImg" />

        <div class="cta-card__content">
          <RawHtml
            tag="h3"
            class="cta-card__title"
            :html="{ before: formatString($page.ctaProductsTitle) }"
          />
          <p>{{ $page.ctaProductsText }}</p>
          <LangRouterLink
            :to="{ name: 'product.index' }"
            class="button -border -link -alt cta-card__link"
            >{{ $page.ctaProductsLinkLabel }}
          </LangRouterLink>
        </div>
      </div>

      <div v-scroll:reveal="{ delay: 100 }" class="cta-card">
        <Image v-if="$page.ctaValuesImg" class="cta-card__image" :source="$page.ctaValuesImg" />

        <div class="cta-card__content">
          <RawHtml
            tag="h3"
            class="cta-card__title"
            :html="{ before: formatString($page.ctaValuesTitle) }"
          />
          <p>{{ $page.ctaValuesText }}</p>
          <LangRouterLink :to="{ name: 'about' }" class="button -border -link -alt cta-card__link"
            >{{ $page.ctaValuesLinkLabel }}
          </LangRouterLink>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CardSlider from '@/components/CardSlider.vue'
import ContentSlider from '@/components/ContentSlider.vue'
import Image from '@/components/global/Image.vue'
import LangRouterLink from '@/components/global/LangRouterLink.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import MobileContent from '@/components/MobileContent.vue'
import Modal from '@/components/Modal.vue'
import { formatString } from '@/helpers/conversionHelpers.js'
import PageMetaMixin from '@/mixins/PageMetaMixin.js'

export default {
  name: 'Sustainability',
  beetPage: 'sustainability',
  mixins: [PageMetaMixin],
  setup: () => ({ formatString }),
  components: { Image, CardSlider, ContentSlider, MobileContent, Modal, LangRouterLink, RawHtml },
  data() {
    return {
      currentPriorityIndex: 0,
      currentFrameworkIndex: 0,
    }
  },
  computed: {
    currentPrioritySlide() {
      if (!this.$page.prioritySlides) return {}
      return this.$page.prioritySlides[this.currentPriorityIndex]
    },
    currentFrameworkSlide() {
      if (!this.$page.frameworkSlides) return {}
      return this.$page.frameworkSlides[this.currentFrameworkIndex]
    },
  },
  methods: {
    handlePriorityIndexChange(index) {
      this.currentPriorityIndex = index
    },
    handleFrameworkIndexChange(index) {
      this.currentFrameworkIndex = index
    },
  },
}
</script>
